import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { camelizeKeys } from 'humps';
import moment from 'moment';
import 'moment/locale/it';

import { useAuth0 } from '../../context/auth0';
import endpoints from '../../define/endpoints';
import Select, { components } from 'react-select';
import { BREAKPOINTS } from '../../../constants';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import OrderTease from '../Order/OrderTease';
import BetaIcon, { IconSizes } from '../Icons/Icon';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import Button, {
  ButtonSizes,
  ButtonStyles,
  ButtonWidths,
} from '../Button/Button';
import FaqTopic from './FaqTopic';
import LoginSection from './LoginSection';
import Skeleton from '../../ui-components/Skeleton/Skeleton';
import { TRANSLATIONS } from '../../../translations';
import ModalDrawer from '../modals/ModalDrawer';
import OrderDetails from '../Order/OrderDetails';

import { ModalSessionExpired } from './../modals/ModalSessionExpired';
import ReturnOrderButtons from '../OrderList/SubComponents/ReturnOrderButtons';
import { ModalConfirmedVoucherToGiftcard } from '../modals/ModalConfirmedVoucherToGiftcard';
import { ModalCancelConfirmedVoucher } from '../modals/ModalCancelConfirmedVoucher';
import { ModalDownloadFile } from '../modals/ModalDownloadFile';
import { ModalBookingCompletedRefund } from '../modals/ModalBookingCompletedRefund';
import { ModalBookingCompletedReschedule } from '../modals/ModalBookingCompletedReschedule';
import ModalOrderIssue from '../modals/ModalOrderIssue';
import { ModalBookingCompletedToVoucher } from '../modals/ModalBookingCompletedToVoucher';
import { ModalCancelGiftcard } from '../modals/ModalCancelGiftcard';
import { ModalResendEmail } from '../modals/ModalResendEmail';
import { ModalNotCompletedToVoucher } from '../modals/ModalNotCompletedToVoucher';
import { ModalNotCompletedReschedule } from '../modals/ModalNotCompletedReschedule';
import { ModalNotCompletedToGiftcard } from '../modals/ModalNotCompletedToGiftcard';
import { ModalDeleteBookingNotCompleted } from '../modals/ModalDeleteBookingNotCompleted';
import { canShowPartnerInfo } from '../../utils/canShowPartnerInfo';
import FaqSearch from './FaqSearch';

const ManageOrder = ({ orderFaq, hubspotFormType, phoneLink }) => {
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);
  const cardRef = useRef(null);

  const parsedFaq = useMemo(() => {
    try {
      return JSON.parse(orderFaq) || [];
    } catch (e) {
      return [];
    }
  }, [orderFaq]);

  const [orders, setOrders] = useState(null);
  const [order, setOrder] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [requestDetails, setRequestDetails] = useState(null);

  function transformOrder(order) {
    return {
      ...order,
      bookingDatetime: moment(order.bookingDatetime),
      createdAt: moment(order.createdAt),
      refunds: order.refunds
        ? order.refunds.map((refund) => ({
          ...refund,
          date: moment(refund.date),
        }))
        : null,
      voucherExpiry: moment(order.voucherExpiry),
    };
  }

  useEffect(() => {
    if (!!order && isDeviceSmallerThanTablet && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [order]);

  useEffect(() => {
    if (openDetails) {
      document.body.classList.add('noscroll');
    } else {
      document.body.classList.remove('noscroll');
    }

    return () => {
      document.body.classList.remove('noscroll');
    };
  }, [openDetails]);

  useEffect(() => {
    if (requestDetails) {
      const order = transformOrder(camelizeKeys(requestDetails));
      setOrders((prevItems) =>
        prevItems.map((item) => (item.id === order.id ? { ...order } : item))
      );
      setOrder({ ...order });
      setRequestDetails(null);
    }
  }, [requestDetails]);

  const [orderOptions, setOrderOptions] = useState([]);
  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading,
    token,
    loginWithRedirect,
  } = useAuth0();

  const userEmail = user && user.email;

  const shouldShowLoginPrompt = !isAuthenticated && !isAuthLoading && !orders;

  //modals
  ////modals-success
  const [isModalDeleteBookingOnHoldOpen, setIsModalDeleteBookingOnHoldOpen] =
    useState(false);

  ////modal actions
  const [isModalTransformVoucherOpen, setIsModalTransformVoucherOpen] =
    useState(false);
  const [isModalCancelVoucherOpen, setIsModalCancelVoucherOpen] =
    useState(false);
  const [isModalDownloadFileOpen, setIsModalDownloadFileOpen] = useState(false);
  const [isModalRefundOpen, setIsModalRefundOpen] = useState(false);
  const [isModalRescheduleOpen, setIsModalRescheduleOpen] = useState(false);
  const [isModalOrderIssueOpen, setIsModalOrderIssueOpen] = useState(false);
  const [
    isModalBookingCompletedToVoucher,
    setIsModalBookingCompletedToVoucher,
  ] = useState(false);
  const [isModalCancelGiftcardOpen, setIsModalCancelGiftcardOpen] =
    useState(false);
  const [isModalResendEmailOpen, setIsModalResendEmailOpen] = useState(false);
  const [isModalOnHoldToVoucher, setIsModalOnHoldToVoucher] = useState(false);
  const [isModalOnHoldRescheduleOpen, setIsModalOnHoldRescheduleOpen] =
    useState(false);
  const [isModalOnHoldToGiftcardOpen, setIsModalOnHoldToGiftcardOpen] =
    useState(false);

  useEffect(() => {
    if (!token) return;

    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${endpoints.core}/orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { locale: window.freedomeConfig.locale },
        });

        const data = camelizeKeys(res.data).map((order) => ({
          ...order,
          bookingDatetime: moment(order.bookingDatetime),
          createdAt: moment(order.createdAt),
          refunds: order.refunds
            ? order.refunds.map((refund) => ({
              ...refund,
              date: moment(refund.date),
            }))
            : null,
          voucherExpiry: moment(order.voucherExpiry),
        }));

        setOrders(data || []);

        const options = data.map((order) => ({
          value: order.id,
          label: `#${order.id} - ${order.listing?.title || ''}`,
        }));

        setOrderOptions(options);
      } catch (err) {
        // console.log(err);
      }
    };

    fetchOrders();
  }, [isAuthenticated, token]);

  // early returns
  if (shouldShowLoginPrompt) {
    return <LoginSection />;
  }

  if (!orders) {
    return (
      <div className='padded-wrapper'>
        <div className="cell" style={{ flex: '1 1 430px' }}>
          <Skeleton classNames="radius-xs mb-m" width="100%" height="22px" />
          <Skeleton classNames="radius-rounded" width="100%" height="38px" />
        </div>
      </div>
    );
  }

  if (orders && !orders.length) {
    return (
      <div className="cell">
        <div className="d-flex items-center flex-column ">
          <p>{TRANSLATIONS.YOU_DONT_HAVE_ACTIVE_BOOKINGS}</p>

          <Button
            size={ButtonSizes.m}
            style={ButtonStyles.secondary}
            label={
              <Typography
                variant={TypographyVariants.p3}
                weight={TypographyWeights.bold}
              >
                {TRANSLATIONS.FAVOURITE_FIND}
              </Typography>
            }
            redirect={'/'}
          />
        </div>
      </div>
    );
  }

  // authenticated with orders
  return (
    <div className="cell">
      <div className='padded-wrapper'>
        <Typography
          classNames={'pb-m'}
          variant={TypographyVariants.p2}
          weight={TypographyWeights.bold}
        >
          {TRANSLATIONS.SELECT_ORDER_TO_GET_HELP}
        </Typography>
        <div className="input-wrapper manage-order-search">
          <Select
            placeholder={TRANSLATIONS.SELECT_ORDER}
            loadingMessage={() => ''}
            isSearchable={false}
            classNamePrefix="manage-order-search"
            options={orderOptions}
            onChange={(option) => {
              let selectedOrder = orders.find(
                (order) => order.id === option.value
              );
              setOrder(selectedOrder);
            }}
            value={
              order ? orderOptions.find((opt) => opt.value === order.id) : null
            }
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: ({ selectProps }) => {
                const isOpen = selectProps.menuIsOpen;
                return (
                  <div style={{ paddingRight: '12px' }}>
                    <BetaIcon
                      name={isOpen ? 'chevronUp' : 'chevronDown'}
                      size={IconSizes[20]}
                    />
                  </div>
                );
              },
            }}
          />
        </div>
      </div>
      {order && (
        <>
          <div ref={cardRef} className="manage-order__columns_wrapper">
            <OrderTease
              className={'vertical-card padded-wrapper'}
              key={order.id}
              order={order}
              showMeetingPoint={true}
              //onClick={setOrder}
              buttons={
                <ReturnOrderButtons
                  token={token}
                  order={order}
                  setOrder={setOrder}
                  onClickDetails={() => setOpenDetails(true)}
                  setIsModalTransformVoucherOpen={
                    setIsModalTransformVoucherOpen
                  }
                  setIsModalCancelVoucherOpen={setIsModalCancelVoucherOpen}
                  setIsModalDownloadFileOpen={setIsModalDownloadFileOpen}
                  setIsModalRefundOpen={setIsModalRefundOpen}
                  setIsModalRescheduleOpen={setIsModalRescheduleOpen}
                  setIsModalOrderIssueOpen={setIsModalOrderIssueOpen}
                  setIsModalBookingCompletedToVoucherOpen={
                    setIsModalBookingCompletedToVoucher
                  }
                  setIsModalCancelGiftcardOpen={setIsModalCancelGiftcardOpen}
                  setIsModalResendEmailOpen={setIsModalResendEmailOpen}
                  setIsModalDeleteBookingOnHoldOpen={
                    setIsModalDeleteBookingOnHoldOpen
                  }
                  setIsModalOnHoldToVoucherOpen={setIsModalOnHoldToVoucher}
                  setIsModalOnHoldRescheduleOpen={
                    setIsModalOnHoldRescheduleOpen
                  }
                  setIsModalOnHoldToGiftcardOpen={
                    setIsModalOnHoldToGiftcardOpen
                  }
                  setRequestDetails={setRequestDetails}
                />
              }
            />
            <div className="manage-order__oder-faq">
              <FaqSearch
                faqs={orderFaq}
                hubspotFormType={hubspotFormType}
                phoneLink={phoneLink}
                partnerInfo={order?.partner}
                canShowPartnerInfo={canShowPartnerInfo(order?.status)}
              />
            </div>
          </div>
          <ModalDrawer
            isOpen={openDetails}
            modalCustomWidth={640}
            setIsOpen={() => setOpenDetails(false)}
            isDeviceSmallerThanTablet={isDeviceSmallerThanTablet}
            title={TRANSLATIONS.RECAP}
            content={
              <div className="pt-m pl-m pr-m pb-m text-left w-100%">
                <OrderDetails
                  order={order}
                  onCloseClick={() => setOpenDetails(false)}
                />
              </div>
            }
          />
        </>
      )}
      {token && order && (
        <>
          <ModalSessionExpired isOpen={!isAuthenticated && !isAuthLoading} />

          <ModalConfirmedVoucherToGiftcard
            isOpen={isModalTransformVoucherOpen}
            setIsOpen={setIsModalTransformVoucherOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalCancelConfirmedVoucher
            isOpen={isModalCancelVoucherOpen}
            setIsOpen={setIsModalCancelVoucherOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />

          <ModalCancelGiftcard
            isOpen={isModalCancelGiftcardOpen}
            setIsOpen={setIsModalCancelGiftcardOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />

          <ModalResendEmail
            isOpen={isModalResendEmailOpen}
            setIsOpen={setIsModalResendEmailOpen}
            order={order}
            token={token}
            userEmail={userEmail}
          />
          <ModalDownloadFile
            isOpen={isModalDownloadFileOpen}
            setIsOpen={setIsModalDownloadFileOpen}
            order={order}
            token={token}
          />

          <ModalBookingCompletedRefund
            isOpen={isModalRefundOpen}
            setIsOpen={setIsModalRefundOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalBookingCompletedReschedule
            isOpen={isModalRescheduleOpen}
            setIsOpen={setIsModalRescheduleOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalBookingCompletedToVoucher
            isOpen={isModalBookingCompletedToVoucher}
            setIsOpen={setIsModalBookingCompletedToVoucher}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalNotCompletedToVoucher
            isOpen={isModalOnHoldToVoucher}
            setIsOpen={setIsModalOnHoldToVoucher}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />

          <ModalOrderIssue
            isOpen={isModalOrderIssueOpen}
            setIsOpen={setIsModalOrderIssueOpen}
            order={order}
            token={token}
          />

          <ModalDeleteBookingNotCompleted
            isOpen={isModalDeleteBookingOnHoldOpen}
            setIsOpen={setIsModalDeleteBookingOnHoldOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalNotCompletedReschedule
            isOpen={isModalOnHoldRescheduleOpen}
            setIsOpen={setIsModalOnHoldRescheduleOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
          <ModalNotCompletedToGiftcard
            isOpen={isModalOnHoldToGiftcardOpen}
            setIsOpen={setIsModalOnHoldToGiftcardOpen}
            order={order}
            token={token}
            setRequestDetails={setRequestDetails}
          />
        </>
      )}
    </div>
  );
};

ManageOrder.propTypes = {};

export default ManageOrder;
